import LoadingIcon from "components/loading-icon";
import useLanguage from "hooks/language";
import React, {
    CSSProperties,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Appointment, { FollowUpType } from "../../models/appointment";
import Clinic from "../../models/clinic";
import ClinicCard from "./clinic-card";
import styles from "./clinic-list.module.css";
import FilterOptionButton from "components/filter-option-button";
import {
    sortClinicsByNextAvailableTime,
    sortClinicsByDefaultOrder,
    sortClinicsByDistance,
} from "./clinic-sorting-functions";
import { Icon } from "react-feather";
import useAnalytics from "../../hooks/analytics";
import { trackBookingFlowClinicListFilterChanged } from "utils/analytics/booking-flow-tracking";
import { UserActionEvent } from "utils/analytics/events";

const SELECT_ALL_CLINICS = "all";

const DISTANCE_THRESHOLD_IN_METERS = 35_000;

type ClinicWithDistance = Clinic & { distanceInMeters: number };

function allHaveDistance(clinics: Clinic[]): clinics is ClinicWithDistance[] {
    // distanceInMeters is always either a number or null for all clinics
    return clinics.every((c) => c.distanceInMeters !== undefined);
}

export default function ClinicList({
    clinics,
    appointment,
    selectedClinicID,
    setSelectedClinicID,
    onDateSelect,
    style,
    startDate,
    isDisplayedInModal = false,
    isModalOpen,
    selectedFollowUpType,
    UnSelectedStateIcon,
    scrollContainerRef,
    trackBookingFlowClinicListFilter = false,
}: {
    clinics: Clinic[];
    appointment?: Appointment;
    isVisible?: boolean;
    selectedClinicID: number | null;
    setSelectedClinicID: (clinicId: number | null) => void;
    onDateSelect?: (datetime: string | null) => void;
    style?: CSSProperties;
    startDate?: string;
    isDisplayedInModal?: boolean;
    isModalOpen?: boolean;
    selectedFollowUpType?: FollowUpType;
    UnSelectedStateIcon?: Icon;
    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    trackBookingFlowClinicListFilter?: boolean;
}): React.ReactElement {
    const clinicsDefaultOrder = [...clinics].sort(sortClinicsByDefaultOrder);

    const [selectedCity, setSelectedCity] = useState<string>(
        SELECT_ALL_CLINICS
    );
    const [analytics] = useAnalytics();

    const selectedClinicCardRef = useRef<HTMLDivElement | null>(null);
    const didInitialScroll = useRef(false);

    const { translations } = useLanguage();
    const allClinics = translations.clinicSelection.all;

    const onSelectCity = (cityName: string) => {
        cityName === allClinics
            ? setSelectedCity(SELECT_ALL_CLINICS)
            : setSelectedCity(cityName);
        if (trackBookingFlowClinicListFilter) {
            trackBookingFlowClinicListFilterChanged(analytics, cityName);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ClinicListFilterChanged, {
                    city: cityName,
                });
        }
    };

    const cityList = Array.from(
        new Set(clinicsDefaultOrder.filter((c) => c.city).map((c) => c.city))
    ).sort();

    cityList.unshift(allClinics);

    const sortedClinics = useMemo<Clinic[]>(() => {
        let result = [...clinicsDefaultOrder];
        result.sort(sortClinicsByDistance);

        if (selectedCity !== SELECT_ALL_CLINICS) {
            result = result.filter((clinic) => clinic.city === selectedCity);
            return result.sort(sortClinicsByNextAvailableTime);
        }

        if (allHaveDistance(result)) {
            const withinDistanceThreshold = result
                .filter(
                    (c) => c.distanceInMeters <= DISTANCE_THRESHOLD_IN_METERS
                )
                .sort(sortClinicsByNextAvailableTime);

            const outsideDistanceThreshold = result.filter(
                (c) => c.distanceInMeters > DISTANCE_THRESHOLD_IN_METERS
            );
            result = [...withinDistanceThreshold, ...outsideDistanceThreshold];
        }

        return result;
    }, [clinicsDefaultOrder, selectedCity]);

    useEffect(() => {
        if (isDisplayedInModal && isModalOpen) {
            didInitialScroll.current = false;
        }
    }, [isDisplayedInModal, isModalOpen]);

    useEffect(() => {
        if (!selectedClinicID) return;
        if (didInitialScroll.current) return;
        if (isDisplayedInModal && !isModalOpen) return;

        const clinicCard = selectedClinicCardRef.current;
        if (!clinicCard) return;

        requestAnimationFrame(() => {
            const scrollContainerEl = scrollContainerRef?.current ?? null;

            // modal scroll
            if (scrollContainerEl) {
                const top =
                    clinicCard.getBoundingClientRect().top -
                    scrollContainerEl.getBoundingClientRect().top +
                    scrollContainerEl.scrollTop;

                scrollContainerEl.scrollTo({
                    top,
                    behavior: "smooth",
                });
            } else {
                // page scroll
                clinicCard.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }

            didInitialScroll.current = true;
        });
    }, [selectedClinicID, sortedClinics.length, scrollContainerRef]);

    return (
        <>
            {clinicsDefaultOrder.length === 0 || !clinicsDefaultOrder ? (
                <div className={styles.loadingIconContainer}>
                    <LoadingIcon />
                </div>
            ) : (
                <div
                    className={
                        isDisplayedInModal ? "" : styles.clinicListContainer
                    }
                >
                    <div
                        role="group"
                        aria-label={
                            translations.clinicSelection.cityList.ariaLabel
                        }
                        className={styles.clinicCitySelectionBox}
                    >
                        {cityList.map((city) => (
                            <FilterOptionButton
                                key={city}
                                option={city}
                                isSelected={
                                    selectedCity === city ||
                                    (selectedCity === SELECT_ALL_CLINICS &&
                                        city === allClinics)
                                }
                                onSelect={onSelectCity}
                            />
                        ))}
                    </div>
                    {sortedClinics.map((clinic) => (
                        <ClinicCard
                            clinic={clinic}
                            key={clinic.id}
                            selectedClinicID={selectedClinicID}
                            setSelectedClinicID={setSelectedClinicID}
                            style={style}
                            appointment={appointment}
                            onDateSelect={onDateSelect}
                            startDate={startDate}
                            UnSelectedStateIcon={UnSelectedStateIcon}
                            containerRef={
                                clinic.id === selectedClinicID
                                    ? selectedClinicCardRef
                                    : undefined
                            }
                        />
                    ))}
                    {selectedFollowUpType && (
                        <p className={styles.serviceDescription}>
                            {
                                translations.appointmentConfirmation
                                    .serviceDescription[selectedFollowUpType]
                            }
                        </p>
                    )}
                </div>
            )}
        </>
    );
}
